import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeading } from "@/components/site/primitives";
import { StudentExperience } from "@/components/home/StudentExperience";
import { ABOUT, IMAGES } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useSEO } from "@/hooks/useSite";
import { Target, Eye, Compass } from "lucide-react";

export default function About() {
  const { config } = useSiteConfig();
  useSEO({
    title: "About | IAUE School of Basic Studies, Port Harcourt",
    description: "About the School of Basic Studies, Ignatius Ajuru University of Education — who we are, our mission, vision and approach to academic preparation.",
  });
  return (
    <>
      <PageHero overline="About the School" title="An Established Foundation Within IAUE"
        subtitle="The School of Basic Studies is an academic environment focused on preparing students for the next stage of their education."
        crumb="About" image={IMAGES.campus} />

      <section className="py-20 sm:py-28 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-[#0A1E4E]/15 bg-[#0A1E4E] p-2">
              <img src={IMAGES.collaborating} alt="IAUE School of Basic Studies flyer" className="w-full rounded-sm object-contain" />
            </div>
          </Reveal>
          <div>
            <span className="overline text-[#C0202A]">Who We Are</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-[#0A1E4E] leading-tight">
              A serious academic environment, associated with Ignatius Ajuru University of Education.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{ABOUT.who}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{ABOUT.approach}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F7F5EF]">
        <div className="container-x">
          <SectionHeading overline="What Drives Us" title="Mission, Vision & Approach" align="center" />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, t: "Our Mission", d: config.mission },
              { icon: Eye, t: "Our Vision", d: config.vision },
              { icon: Compass, t: "Our Approach", d: ABOUT.approach },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="h-full rounded-sm border border-slate-200 bg-white p-8">
                  <div className="flex h-14 w-14 items-center justify-center rounded-sm bg-[#0A1E4E] text-[#D4AF37]">
                    <c.icon size={26} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-bold text-[#0A1E4E]">{c.t}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 rounded-sm border-l-4 border-[#D4AF37] bg-white p-7">
              <h3 className="font-serif text-xl font-bold text-[#0A1E4E]">Why the School Exists</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{ABOUT.why}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <StudentExperience />
    </>
  );
}
